import React from 'react';
import { Text, LegacyStack } from '@shopify/polaris';
import { defaultTranslations } from '../../services/translationsService';

/**
 * Component to visualize fit type and stretch level with sliders
 */
export default function FitGuideVisualizer({ 
  fitType: rawFitType = 'regular', 
  stretchLevel: rawStretchLevel = 'none', 
  fontFamily = 'sans-serif', 
  showLabels = true,
  // Style customization props
  dotColor = '#000000',
  trackColor = '#e6e6e6',
  labelColor = '#637381',

  // Shape customization
  indicatorShape = 'dot', // 'dot', 'rectangle', 'triangle', or 'diamond'
  indicatorSize = 16, // Size in pixels
  indicatorOffset = 0, // Vertical offset in pixels (positive = down, negative = up)
  // Translation props
  language = 'en',
  translations = null,
  // Hide heading to avoid duplication
  hideHeading = false,
  // Prevent adding 'Fit' to labels (especially for German)
  preventFitSuffix = false
}) {
  // Normalize the fit type and stretch level values to ensure they match the keys in the positions objects
  // This helps with case sensitivity and whitespace issues
  const fitType = typeof rawFitType === 'string' ? rawFitType.trim().toLowerCase() : 'regular';
  const stretchLevel = typeof rawStretchLevel === 'string' ? rawStretchLevel.trim().toLowerCase() : 'none';

  // Debug the incoming props
  console.log('FitGuideVisualizer received props:', { rawFitType, rawStretchLevel });
  console.log('Normalized values:', { fitType, stretchLevel });

  // Define the exact positions for each label
  const fitTypePositions = {
    'slim': 0,        // Far left position
    'regular': 33,    // Left-center position
    'relaxed regular': 50, // Between regular and loose
    'loose': 67,      // Right-center position
    'oversized': 100  // Far right position
  };
  
  const stretchPositions = {
    'none': 0,      // Far left position
    'slight': 25,   // Left-center position
    'moderate': 50, // Center position
    'stretchy': 75, // Right-center position
    'high': 100     // Far right position
  };
  
  console.log('Fit type positions:', fitTypePositions);
  console.log('Stretch positions:', stretchPositions);
  
  // Map fit type to a position on the scale (0-100)
  const getFitTypePosition = () => {
    // If fitType is null, don't show a position (AI hasn't determined it yet)
    if (fitType === null) return -50; // Position it off-screen
    
    console.log('Getting position for fit type:', fitType);
    console.log('Is fit type in positions object?', fitType in fitTypePositions);
    console.log('Position value:', fitTypePositions[fitType]);
    
    // Return the exact position for this fit type
    return fitTypePositions[fitType] || 50; // Default to center if not found
  };

  // Map stretch level to a position on the scale (0-100)
  const getStretchPosition = () => {
    // If stretchLevel is null, don't show a position (AI hasn't determined it yet)
    if (stretchLevel === null) return -50; // Position it off-screen
    
    console.log('Getting position for stretch level:', stretchLevel);
    console.log('Is stretch level in positions object?', stretchLevel in stretchPositions);
    console.log('Position value:', stretchPositions[stretchLevel]);
    
    // Return the exact position for this stretch level
    return stretchPositions[stretchLevel] || 0; // Default to left if not found
  };

  // Get label for fit type
  const getFitTypeLabel = () => {
    console.log('Getting fit type label for:', fitType, 'in language:', language);
    
    if (fitType === null) return 'To be determined by AI';
    
    // Use custom translations if available
    if (translations && translations.fitTypes && translations.fitTypes[fitType]) {
      console.log('Using custom translation for fit type:', translations.fitTypes[fitType]);
      return translations.fitTypes[fitType];
    }
    
    // Use default translations based on language
    if (defaultTranslations.elements[language] && 
        defaultTranslations.elements[language][fitType]) {
      console.log('Using default translation for fit type:', defaultTranslations.elements[language][fitType]);
      return defaultTranslations.elements[language][fitType];
    } else {
      console.log('No default translation found for fit type in language:', language);
    }
    
    // Fallback to English
    console.log('Falling back to English for fit type:', fitType);
    switch (fitType) {
      case 'slim': return 'Slim';
      case 'regular': return 'Regular';
      case 'loose': return 'Loose';
      case 'oversized': return 'Oversized';
      default: return 'Regular';
    }
  };

  // Get label for stretch level
  const getStretchLabel = () => {
    console.log('Getting stretch label for:', stretchLevel, 'in language:', language);
    
    if (stretchLevel === null) return 'To be determined by AI';
    
    // Use custom translations if available
    if (translations && translations.stretchLevels && translations.stretchLevels[stretchLevel]) {
      console.log('Using custom translation for stretch level:', translations.stretchLevels[stretchLevel]);
      return translations.stretchLevels[stretchLevel];
    }
    
    // Use default translations based on language
    if (defaultTranslations.elements[language] && 
        defaultTranslations.elements[language][stretchLevel]) {
      console.log('Using default translation for stretch level:', defaultTranslations.elements[language][stretchLevel]);
      return defaultTranslations.elements[language][stretchLevel];
    } else {
      console.log('No default translation found for stretch level in language:', language);
    }
    
    // Fallback to English
    console.log('Falling back to English for stretch level:', stretchLevel);
    switch (stretchLevel) {
      case 'none': return 'No stretch';
      case 'slight': return 'Slightly stretchy';
      case 'moderate': return 'Moderate stretch';
      case 'stretchy': return 'Stretchy';
      case 'high': return 'Highly stretchy';
      default: return 'No stretch';
    }
  };
  
  // Render the appropriate indicator shape
  const renderIndicator = (position) => {
    // Calculate the adjusted top position with the offset
    const topPosition = `calc(50% + ${indicatorOffset}px)`;
    
    const baseStyle = {
      position: 'absolute',
      left: `${position}%`,
      top: topPosition,
      transform: 'translate(-50%, -50%)',
      backgroundColor: dotColor,
      zIndex: 5, // Increased z-index to ensure visibility
      boxShadow: '0 0 2px rgba(0,0,0,0.2)', // Subtle shadow for better visibility
      border: '1px solid rgba(255,255,255,0.5)' // Light border to help with contrast
    };
    
    switch (indicatorShape) {
      case 'rectangle':
        return (
          <div style={{
            ...baseStyle,
            width: indicatorSize,
            height: indicatorSize * 0.8,
            borderRadius: '2px',
            transform: 'translate(-50%, -50%)'
          }} />
        );
      case 'triangle':
        return (
          <div style={{
            position: 'absolute',
            left: `${position}%`,
            top: topPosition,
            width: 0,
            height: 0,
            backgroundColor: 'transparent',
            borderLeft: `${indicatorSize/2}px solid transparent`,
            borderRight: `${indicatorSize/2}px solid transparent`,
            borderTop: `${indicatorSize}px solid ${dotColor}`,
            transform: 'translate(-50%, -30%)',
            zIndex: 5,
            filter: 'drop-shadow(0px 0px 1px rgba(0,0,0,0.3))' // Add shadow for better visibility
          }} />
        );
      case 'diamond':
        return (
          <div style={{
            position: 'absolute',
            left: `${position}%`,
            top: topPosition,
            width: indicatorSize,
            height: indicatorSize,
            backgroundColor: dotColor,
            transform: 'translate(-50%, -50%) rotate(45deg)',
            zIndex: 5,
            boxShadow: '0 0 2px rgba(0,0,0,0.2)',
            border: '1px solid rgba(255,255,255,0.5)'
          }} />
        );
      case 'dot':
      default:
        return (
          <div style={{
            ...baseStyle,
            width: indicatorSize,
            height: indicatorSize,
            borderRadius: '50%'
          }} />
        );
    }
  };

  return (
    <div style={{ fontFamily }}>
      {/* Fit Type Slider */}
      <div style={{ marginBottom: '20px' }}>
        {showLabels && !hideHeading && <Text variant="headingSm" style={{ color: labelColor }}>
          {defaultTranslations.elements[language]?.fitGuide || 'Fit Type'} {fitType === null && '(Will be determined by AI)'}
        </Text>}
        <div style={{ 
          position: 'relative', 
          height: '8px', 
          backgroundColor: trackColor, 
          borderRadius: '4px',
          marginTop: '16px',
          marginBottom: '16px',
          overflow: 'visible' // Changed to visible so shapes appear on top
        }}>
          {fitType !== null && renderIndicator(getFitTypePosition())}
        </div>
        <div style={{ display: 'flex', position: 'relative', width: '100%', height: '30px', textAlign: 'center' }}>
          {/* Slim label - positioned at 0% */}
          <div style={{ position: 'absolute', left: '0%', transform: 'translateX(-50%)', width: '100px', textAlign: 'center' }}>
            <Text variant="bodyMd" style={{ color: labelColor }}>
              {/* Use the translation without adding 'Fit' */}
              {(translations && translations.fitTypes && translations.fitTypes.slim) || 
               (defaultTranslations.elements[language]?.slim) || 
               'Slim'}
            </Text>
          </div>
          
          {/* Regular label - positioned at 33% */}
          <div style={{ position: 'absolute', left: '33%', transform: 'translateX(-50%)', width: '100px', textAlign: 'center' }}>
            <Text variant="bodyMd" style={{ color: labelColor }}>
              {(translations && translations.fitTypes && translations.fitTypes.regular) || 
               (defaultTranslations.elements[language]?.regular) || 
               'Regular'}
            </Text>
          </div>
          
          {/* Loose label - positioned at 67% */}
          <div style={{ position: 'absolute', left: '67%', transform: 'translateX(-50%)', width: '100px', textAlign: 'center' }}>
            <Text variant="bodyMd" style={{ color: labelColor }}>
              {(translations && translations.fitTypes && translations.fitTypes.loose) || 
               (defaultTranslations.elements[language]?.loose) || 
               'Loose'}
            </Text>
          </div>
          
          {/* Oversized label - positioned at 100% */}
          <div style={{ position: 'absolute', left: '100%', transform: 'translateX(-50%)', width: '100px', textAlign: 'center' }}>
            <Text variant="bodyMd" style={{ color: labelColor }}>
              {(translations && translations.fitTypes && translations.fitTypes.oversized) || 
               (defaultTranslations.elements[language]?.oversized) || 
               'Oversized'}
            </Text>
          </div>
        </div>
      </div>

      {/* Stretch Level Slider */}
      <div>
        {showLabels && !hideHeading && <Text variant="headingSm" style={{ color: labelColor }}>
          {defaultTranslations.elements[language]?.stretchLevel || 'Stretch'} 
          {stretchLevel === null && '(Will be determined by AI)'}
        </Text>}
        <div style={{ 
          position: 'relative', 
          height: '8px', 
          backgroundColor: trackColor, 
          borderRadius: '4px',
          marginTop: '16px',
          marginBottom: '16px',
          overflow: 'visible' // Changed to visible so shapes appear on top
        }}>
          {stretchLevel !== null && renderIndicator(getStretchPosition())}
        </div>
        <div style={{ display: 'flex', position: 'relative', width: '100%', height: '30px', textAlign: 'center' }}>
          {/* No stretch label - positioned at 0% */}
          <div style={{ position: 'absolute', left: '0%', transform: 'translateX(-50%)', width: '100px', textAlign: 'center' }}>
            <Text variant="bodyMd" style={{ color: labelColor }}>
              {(translations && translations.stretchLevels && translations.stretchLevels.none) || 
               (defaultTranslations.elements[language]?.none) || 
               'No stretch'}
            </Text>
          </div>
          
          {/* Slightly label - positioned at 25% */}
          <div style={{ position: 'absolute', left: '25%', transform: 'translateX(-50%)', width: '100px', textAlign: 'center' }}>
            <Text variant="bodyMd" style={{ color: labelColor }}>
              {(translations && translations.stretchLevels && translations.stretchLevels.slight && 
                translations.stretchLevels.slight.split(' ')[0]) || 
               (defaultTranslations.elements[language]?.slight && 
                defaultTranslations.elements[language].slight.split(' ')[0]) || 
               'Slightly'}
            </Text>
          </div>
          
          {/* Moderate label - positioned at 50% */}
          <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', width: '100px', textAlign: 'center' }}>
            <Text variant="bodyMd" style={{ color: labelColor }}>
              {(translations && translations.stretchLevels && translations.stretchLevels.moderate && 
                translations.stretchLevels.moderate.split(' ')[0]) || 
               (defaultTranslations.elements[language]?.moderate && 
                defaultTranslations.elements[language].moderate.split(' ')[0]) || 
               'Moderate'}
            </Text>
          </div>
          
          {/* Stretchy label - positioned at 75% */}
          <div style={{ position: 'absolute', left: '75%', transform: 'translateX(-50%)', width: '100px', textAlign: 'center' }}>
            <Text variant="bodyMd" style={{ color: labelColor }}>
              {(translations && translations.stretchLevels && translations.stretchLevels.stretchy && 
                translations.stretchLevels.stretchy.split(' ')[0]) || 
               (defaultTranslations.elements[language]?.stretchy && 
                defaultTranslations.elements[language].stretchy.split(' ')[0]) || 
               'Stretchy'}
            </Text>
          </div>
          
          {/* Highly label - positioned at 100% */}
          <div style={{ position: 'absolute', left: '100%', transform: 'translateX(-50%)', width: '100px', textAlign: 'center' }}>
            <Text variant="bodyMd" style={{ color: labelColor }}>
              {(translations && translations.stretchLevels && translations.stretchLevels.high && 
                translations.stretchLevels.high.split(' ')[0]) || 
               (defaultTranslations.elements[language]?.high && 
                defaultTranslations.elements[language].high.split(' ')[0]) || 
               'Highly'}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
